import { Injectable, signal } from '@angular/core';
import { map, Observable, of, throwError, timer } from 'rxjs';
import { Page, UserNotification, UserNotificationListItem } from '@models';
import { UserNotificationsService } from './user-notifications.service';
import { UNREAD_NOTIFICATIONS_POLL_INTERVAL_MS } from './user-notifications.api.service';

@Injectable({ providedIn: 'root' })
export class UserNotificationsMockService implements UserNotificationsService {
  private readonly notifications =
    signal<UserNotification[]>(seedNotifications());

  getNotifications(
    pageNumber: number,
    pageSize: number,
    searchText?: string
  ): Observable<Page<UserNotificationListItem>> {
    const term = searchText?.trim().toLowerCase();
    const all = term
      ? this.notifications().filter(
          x =>
            x.title.toLowerCase().includes(term) ||
            x.body.toLowerCase().includes(term)
        )
      : this.notifications();
    const items = all.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

    return of({
      items,
      totalItems: all.length,
      pageNumber,
      pageSize,
      totalPages: Math.ceil(all.length / pageSize),
    });
  }

  getNotification(id: string): Observable<UserNotification> {
    const notification = this.notifications().find(x => x.id === id);
    return notification
      ? of(notification)
      : throwError(() => new Error('Notification not found'));
  }

  markRead(id: string): Observable<UserNotification> {
    this.notifications.update(all =>
      all.map(x => (x.id === id ? { ...x, isRead: true } : x))
    );

    return this.getNotification(id);
  }

  watchUnreadCount(): Observable<number> {
    return timer(0, UNREAD_NOTIFICATIONS_POLL_INTERVAL_MS).pipe(
      map(() => this.notifications().filter(x => !x.isRead).length)
    );
  }
}

function seedNotifications(): UserNotification[] {
  const now = Date.now();
  const hoursAgo = (hours: number) =>
    new Date(now - hours * 3_600_000).toISOString();

  return [
    {
      id: 'mock-notification-1',
      type: 'IntakeFormCompleted',
      severity: 'Info',
      title: 'Form completed: Karen Nelson',
      preview:
        'Karen Nelson completed Consultation Intake Form on Oct 22, 2026.',
      body: 'Karen Nelson completed Consultation Intake Form on Oct 22, 2026.',
      createdAt: hoursAgo(2),
      isRead: false,
      patientId: 'mock-patient-1',
      linkTarget: 'ConsultationFormResults',
      linkTargetId: 'mock-submission-1',
    },
    {
      id: 'mock-notification-2',
      type: 'MembershipPaymentFailed',
      severity: 'Danger',
      title: 'Membership payment failed: Karen Nelson',
      preview:
        "The $149.00 payment for Karen Nelson's VIP Plan failed on Oct 21, 2026.",
      body: "The $149.00 payment for Karen Nelson's VIP Plan failed on Oct 21, 2026.",
      createdAt: hoursAgo(26),
      isRead: false,
      patientId: 'mock-patient-1',
      linkTarget: 'PatientMembership',
      linkTargetId: 'mock-membership-1',
    },
    {
      id: 'mock-notification-3',
      type: 'AdminMessage',
      severity: 'Info',
      title: 'Message from iCareDx',
      preview: 'Please complete your provider profile before Friday.',
      body: 'Please complete your provider profile before Friday.',
      createdAt: hoursAgo(72),
      isRead: true,
      patientId: null,
      linkTarget: null,
      linkTargetId: null,
    },
  ];
}
